import { t } from '../../../lib/i18n';
import { type AuctionScheduleItem } from '../types';
import {
  UKRAINE_TIMEZONE,
  formatCurrency,
  formatTimeRemaining,
  getBidManagementUrl,
  getEffectiveEndDate,
} from '../utils';

const DATE_FMT = new Intl.DateTimeFormat('uk-UA', {
  timeZone: UKRAINE_TIMEZONE,
  day: '2-digit',
  month: '2-digit',
});
const TIME_FMT = new Intl.DateTimeFormat('uk-UA', {
  timeZone: UKRAINE_TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
});

const DOT: Record<string, string> = {
  today: 'bg-cf-danger',
  thisWeek: 'bg-cf-accent',
  later: 'bg-cf-past',
};

/** One timeline entry — the rail dot plus the closing's card, in Design D tokens. */
export function TimelineCard({
  auction,
  timeframe,
}: {
  auction: AuctionScheduleItem;
  timeframe: 'today' | 'thisWeek' | 'later';
}) {
  const invalid = Number.isNaN(getEffectiveEndDate(auction.end_date).getTime());
  const none = t('auctionSchedule.board.none');
  const dateText = invalid ? none : DATE_FMT.format(new Date(auction.end_date));
  const timeText = invalid ? none : TIME_FMT.format(new Date(auction.end_date));
  const bidCount = auction.bidCount ?? 0;

  return (
    <div className="relative flex items-start gap-3.5">
      <span
        className={`relative z-10 mt-4 h-2 w-2 shrink-0 rounded-cf-xs ${DOT[timeframe]} lg:ml-[26px]`}
      />

      <a
        href={getBidManagementUrl(auction)}
        className="flex flex-1 items-center gap-2.5 rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 hover:border-cf-ink lg:px-[18px]"
      >
        <div className="w-[52px] shrink-0">
          <div className="font-mono text-[13px]">{dateText}</div>
          <div className="text-xs text-cf-ink-subtle">{timeText}</div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold lg:text-sm">{auction.car_name}</div>
          <div className="truncate text-xs text-cf-ink-subtle">
            {auction.listing_id} · {formatTimeRemaining(auction.end_date, t('auctionSchedule.filters.status.ended'))}
          </div>
        </div>

        <div className="hidden shrink-0 text-right lg:block">
          <div className="font-mono text-sm">
            {auction.highestBidAmount ? formatCurrency(auction.highestBidAmount) : none}
          </div>
          <div className={`font-mono text-[11px] ${bidCount > 0 ? 'text-cf-ok' : 'text-cf-ink-subtle'}`}>
            {t('auctionSchedule.board.columns.clientBids')} {bidCount}
          </div>
        </div>
      </a>
    </div>
  );
}
